/**
 * Decisões do bootstrap do bundle de gamificação.
 *
 * O servidor passa a publicar a versão só depois de o payload dela existir. Com
 * isso surge um estado que antes não existia: **evento sem versão publicada**,
 * enquanto o primeiro build não terminou.
 *
 * Extraído em funções puras porque o app não tem infraestrutura de teste de
 * componente — e estas duas decisões, se erradas, apagam o conteúdo do
 * participante ou o deixam com o app vazio para sempre. É o mesmo caminho de
 * `shared/lib/guild/visibility`.
 *
 * Ver a change openspec `gamification-data-async-cache`, grupo 1.
 */

/**
 * Intervalo entre consultas de versão, em milissegundos.
 *
 * Parcela do orçamento acordado de 5 minutos entre alteração no admin e
 * atualização para o participante: 60 s de coalescing + até 5 s de build + este
 * intervalo. Era 360 s, que sozinho estourava o teto mesmo com build
 * instantâneo.
 */
export const VERSION_REFETCH_INTERVAL = 120_000

type MaybeVersion = { version?: string | null } | null | undefined

/**
 * Se o payload deve ser buscado.
 *
 * Versão ausente significa "ainda não há conteúdo publicado", não "buscar de
 * novo": buscar nesse estado rende uma resposta de erro a cada consulta, sem
 * nada a mostrar no fim. Quando o primeiro build terminar, a consulta seguinte
 * de versão traz o valor e a busca acontece então — é assim que o app aberto
 * durante a primeira publicação se hidrata sozinho, sem código de espera.
 */
export const shouldFetchPayload = (
  digitalProductId: string | null | undefined,
  remoteVersion: MaybeVersion,
  localVersion: string | null | undefined
): boolean => {
  if (!digitalProductId) return false

  const published = remoteVersion?.version

  if (!published) return false

  return published !== localVersion
}

/**
 * Se a resposta pode ser gravada no store.
 *
 * O store é persistido: gravar um corpo vazio não deixa o participante um
 * instante sem conteúdo — deixa **sem conteúdo até a próxima publicação**,
 * porque a versão local passa a bater com a remota e nada mais dispara a busca.
 * Por isso a checagem é de conteúdo, não de existência do objeto.
 */
export const isStorablePayload = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false

  return Object.keys(payload as Record<string, unknown>).length > 0
}
